// Centralized account-management directory. Currently rendered for the
// master tier only (see MasterView in App.js). Each row shows a user's
// display name, login email + claim-status badge, and three actions:
// Copy email, Edit email, Set password. Set Password is disabled for
// users who have not yet claimed (no auth account to mutate).
//
// Email changes branch on claim state:
//   claimed   → adminSetUserEmail (edge function: updates auth.users
//                + app-table mirror atomically server-side)
//   unclaimed → updatePreClaimEmail (direct UPDATE on the pre-claim
//                column; no auth user exists yet)

import { useState, useEffect, useCallback } from 'react';
import {
  getAllAdvisors, getAllClients, getAllTeamMembers,
  getClientsForAdvisor, getTeamMembersForAdvisor,
  getTeamMembersForClient,
  adminSetUserEmail, adminSetUserPassword, updatePreClaimEmail,
  deleteAdvisor, deleteClientCascade, deleteTeamMember,
} from './supabase';
import { getDisplayEmail, getClaimStatus } from './lib/userEmail';
import { C, F, Card, useIsMobile } from './tokens';
import { OverflowMenu } from './PortalControls';
import { Users, Compass, UserPlus, Copy, Mail, Key, Eye, EyeOff, Check, Trash2 } from 'lucide-react';

const TYPE_LABEL = { advisor: 'Advisor', client: 'Client', teamMember: 'Team Member' };
const TYPE_ICON = { advisor: Compass, client: Users, teamMember: UserPlus };

function getDisplayName(entry) {
  const { type, row } = entry;
  if (type === 'teamMember') return row.name || '(unnamed)';
  return row.user?.name || row.temp_name || '(unnamed)';
}

function getSecondary(entry) {
  const { type, row } = entry;
  if (type === 'client') return row.company || null;
  if (type === 'teamMember') return row.client?.company || row.job_title || null;
  return null;
}

// Crypto-backed when available; alphabet omits ambiguous chars (0/O, 1/l/I)
// so the password reads cleanly over the phone.
function genRandomPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const len = 12;
  let out = '';
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buf = new Uint32Array(len);
    crypto.getRandomValues(buf);
    for (let i = 0; i < len; i++) out += chars[buf[i] % chars.length];
  } else {
    for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

export default function AdminDirectory({ user, clientId, forceClientScope = false, operatorRole = null }) {
  // forceClientScope: when true, the component always shows ONLY the team
  // members of `clientId`, regardless of user.role. Used when AdminDirectory
  // is mounted INSIDE a client portal (Portal.js), so a master/sales viewer
  // doesn't see their full roster — they see only this client's team.
  // operatorRole: the REAL viewer role before any Preview-mode shim. Used
  // only for action gating (Delete shows for master operator regardless of
  // whether user.role has been shimmed to "client"). Null in non-portal mounts.
  const clientScoped = forceClientScope || user.role === 'client';
  const isMobile = useIsMobile();
  const [advisors, setAdvisors] = useState([]);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [copiedRowKey, setCopiedRowKey] = useState(null);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editingPassword, setEditingPassword] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (forceClientScope) {
        // Forced client scope — mounted inside a client portal. Always
        // load only this client's team members, even for master/sales
        // viewers (whose user.role would otherwise pull a wider roster).
        // Same defensive empty-on-missing-clientId pattern as the client
        // tier branch below.
        if (!clientId) {
          setAdvisors([]); setClients([]); setTeamMembers([]);
        } else {
          const tm = await getTeamMembersForClient(clientId);
          setAdvisors([]);
          setClients([]);
          setTeamMembers(tm || []);
        }
      } else if (user.role === 'client') {
        // Client tier: only own team members. clientId is required —
        // without it we can't scope the query, so show empty rather than
        // failing open to all team members.
        if (!clientId) {
          setAdvisors([]); setClients([]); setTeamMembers([]);
        } else {
          const tm = await getTeamMembersForClient(clientId);
          setAdvisors([]);
          setClients([]);
          setTeamMembers(tm || []);
        }
      } else if (user.role === 'sales') {
        // Advisor tier: only own clients + their team members. Advisors
        // never manage other advisors, so the Advisors pill is hidden below.
        const [cli, tm] = await Promise.all([
          getClientsForAdvisor(user.id),
          getTeamMembersForAdvisor(user.id),
        ]);
        setAdvisors([]);
        setClients(cli || []);
        setTeamMembers(tm || []);
      } else {
        const [adv, cli, tm] = await Promise.all([
          getAllAdvisors(),
          getAllClients(),
          getAllTeamMembers(),
        ]);
        setAdvisors(adv || []);
        setClients(cli || []);
        setTeamMembers(tm || []);
      }
    } catch (e) {
      setLoadError(e.message || String(e));
    }
    setLoading(false);
  }, [user.role, user.id, clientId, forceClientScope]);

  useEffect(() => { reload(); }, [reload]);

  const allEntries = [
    ...advisors.map(r => ({ type: 'advisor', row: r, key: `advisor-${r.id}` })),
    ...clients.map(r => ({ type: 'client', row: r, key: `client-${r.id}` })),
    ...teamMembers.map(r => ({ type: 'teamMember', row: r, key: `tm-${r.id}` })),
  ];

  const visibleEntries = filter === 'all'
    ? allEntries
    : allEntries.filter(e => e.type === filter);

  const counts = {
    advisor: advisors.length,
    client: clients.length,
    teamMember: teamMembers.length,
  };

  function copyEmail(entry) {
    const { email } = getDisplayEmail(entry.row, entry.type);
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopiedRowKey(entry.key);
    setTimeout(() => setCopiedRowKey(prev => (prev === entry.key ? null : prev)), 1500);
  }

  return (
    <div>
      {loadError && (
        <div style={{ ...errBoxStyle(), marginBottom: 14 }}>Failed to load directory: {loadError}</div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        {!clientScoped && (
          <FilterPill label={`All ${allEntries.length}`} active={filter === 'all'} onClick={() => setFilter('all')} />
        )}
        {!clientScoped && user.role !== 'sales' && (
          <FilterPill icon={Compass}  label={`Advisors ${counts.advisor}`}        active={filter === 'advisor'}    onClick={() => setFilter('advisor')} />
        )}
        {!clientScoped && (
          <FilterPill icon={Users}    label={`Clients ${counts.client}`}          active={filter === 'client'}     onClick={() => setFilter('client')} />
        )}
        <FilterPill icon={UserPlus} label={`Team Members ${counts.teamMember}`} active={filter === 'teamMember'} onClick={() => setFilter('teamMember')} />
      </div>

      {loading ? (
        <Card style={{ padding: 30, textAlign: 'center', fontFamily: F.body, color: C.charLight }}>Loading directory…</Card>
      ) : visibleEntries.length === 0 ? (
        <Card style={{ padding: 30, textAlign: 'center', fontFamily: F.body, color: C.charLight }}>No users in this view.</Card>
      ) : isMobile ? (
        // Mobile: per-row cards separated by margin. Plain <div> wrapper —
        // no outer Card chrome and no overflow:hidden, otherwise the
        // OverflowMenu dropdown anchored to each row's kebab would be
        // clipped at the wrapper boundary.
        <div>
          {visibleEntries.map((entry, idx) => (
            <DirectoryRow
              key={entry.key}
              entry={entry}
              isLast={idx === visibleEntries.length - 1}
              isSelf={entry.row.user_id && entry.row.user_id === user.id}
              copied={copiedRowKey === entry.key}
              currentUserRole={user.role}
              operatorRole={operatorRole}
              onCopy={() => copyEmail(entry)}
              onEditEmail={() => setEditingEmail(entry)}
              onSetPassword={() => setEditingPassword(entry)}
              onDelete={() => setDeletingEntry(entry)}
            />
          ))}
        </div>
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          {visibleEntries.map((entry, idx) => (
            <DirectoryRow
              key={entry.key}
              entry={entry}
              isLast={idx === visibleEntries.length - 1}
              isSelf={entry.row.user_id && entry.row.user_id === user.id}
              copied={copiedRowKey === entry.key}
              currentUserRole={user.role}
              operatorRole={operatorRole}
              onCopy={() => copyEmail(entry)}
              onEditEmail={() => setEditingEmail(entry)}
              onSetPassword={() => setEditingPassword(entry)}
              onDelete={() => setDeletingEntry(entry)}
            />
          ))}
        </Card>
      )}

      {editingEmail && (
        <EditEmailModal
          entry={editingEmail}
          onClose={() => setEditingEmail(null)}
          onDone={() => { setEditingEmail(null); reload(); }}
        />
      )}
      {editingPassword && (
        <SetPasswordModal
          entry={editingPassword}
          onClose={() => setEditingPassword(null)}
          onDone={() => { setEditingPassword(null); reload(); }}
        />
      )}
      {deletingEntry && (
        <DeleteModal
          entry={deletingEntry}
          onClose={() => setDeletingEntry(null)}
          onDone={() => { setDeletingEntry(null); reload(); }}
        />
      )}
    </div>
  );
}

function FilterPill({ icon: Icon, label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: active ? C.navyFaint : C.surface,
      color: active ? C.navy : C.charMid,
      border: `1px solid ${active ? C.navyBorder : C.border}`,
      borderRadius: 999, padding: '7px 14px',
      fontFamily: F.body, fontSize: 12, fontWeight: active ? 600 : 500,
      cursor: 'pointer',
    }}>
      {Icon && <Icon size={13} />}
      {label}
    </button>
  );
}

function DirectoryRow({ entry, isLast, isSelf, copied, currentUserRole, operatorRole, onCopy, onEditEmail, onSetPassword, onDelete }) {
  const { type, row } = entry;
  const { email, claimed } = getDisplayEmail(row, type);
  const status = getClaimStatus(row, type);
  const name = getDisplayName(entry);
  const secondary = getSecondary(entry);
  const TypeIcon = TYPE_ICON[type];
  const canSetPassword = !!claimed && !!row.user_id;
  // Delete-gate uses the REAL operator role when provided (operatorRole is
  // set when AdminDirectory is mounted inside a client portal — Preview mode
  // shims user.role to "client", so currentUserRole alone would hide Delete
  // for a master operator who's previewing).
  const effectiveRole = operatorRole || currentUserRole;
  const isMobile = useIsMobile();

  // Name + type-chip + secondary, then mono-email + StatusBadge. Identical
  // in both layouts; the wrapping container differs (lines-not-boxes row
  // on desktop, bordered card on mobile).
  const infoBlock = (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 600, color: C.charcoal }}>{name}</div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontFamily: F.body, fontSize: 10, color: C.charLight,
          textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600,
        }}>
          <TypeIcon size={11} /> {TYPE_LABEL[type]}
        </span>
        {secondary && <span style={{ fontFamily: F.body, fontSize: 11, color: C.charLight }}>· {secondary}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: F.mono, fontSize: 12, color: email ? C.charMid : C.charLight }}>
          {email || '(no email on record)'}
        </span>
        <StatusBadge status={status} />
      </div>
    </div>
  );

  if (isMobile) {
    // Build the applicable-actions list, respecting the same gates as the
    // desktop button cluster. OverflowMenu items support { label, onClick,
    // danger } only — no disabled affordance — so Set password is OMITTED
    // (rather than shown disabled) when the user hasn't claimed yet.
    const actions = [{ label: copied ? 'Copied' : 'Copy email', onClick: onCopy }];
    if (!isSelf) {
      actions.push({ label: 'Edit email', onClick: onEditEmail });
      if (canSetPassword) actions.push({ label: 'Set password', onClick: onSetPassword });
      if (effectiveRole === 'master') actions.push({ label: 'Delete', onClick: onDelete, danger: true });
    }
    return (
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: 13, marginBottom: 12,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        {infoBlock}
        <OverflowMenu items={actions} variant="primary" title={name} subtitle={`Manage account · ${TYPE_LABEL[type]}`}/>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '14px 16px',
      borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
    }}>
      {infoBlock}

      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button title="Copy email" onClick={onCopy} disabled={!email} style={iconBtnStyle(!!email && !copied, copied)}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          <span style={{ marginLeft: 6 }}>{copied ? 'Copied' : 'Copy email'}</span>
        </button>
        {!isSelf && (
          <>
            <button title="Edit email" onClick={onEditEmail} style={iconBtnStyle(true)}>
              <Mail size={14} /><span style={{ marginLeft: 6 }}>Edit email</span>
            </button>
            <button
              title={canSetPassword ? 'Set password' : 'User has not claimed yet'}
              onClick={onSetPassword}
              disabled={!canSetPassword}
              style={iconBtnStyle(canSetPassword)}>
              <Key size={14} /><span style={{ marginLeft: 6 }}>Set password</span>
            </button>
            {effectiveRole === 'master' && (
              <button title="Delete" onClick={onDelete} style={dangerIconBtnStyle()}>
                <Trash2 size={14} /><span style={{ marginLeft: 6 }}>Delete</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      background: isActive ? C.greenBg : C.amberBg,
      color: isActive ? C.green : C.amber,
      border: `1px solid ${isActive ? C.greenBd : C.amberBd}`,
      borderRadius: 999, padding: '2px 10px',
      fontFamily: F.body, fontSize: 10, fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: '.06em',
    }}>{isActive ? 'Active' : 'Invited'}</span>
  );
}

function iconBtnStyle(enabled, success = false) {
  return {
    display: 'inline-flex', alignItems: 'center',
    background: success ? C.greenBg : (enabled ? C.surface : C.bgAlt),
    color: success ? C.green : (enabled ? C.navy : C.charLight),
    border: `1px solid ${success ? C.greenBd : C.border}`,
    borderRadius: 7, padding: '7px 12px',
    fontFamily: F.body, fontSize: 12, fontWeight: 600,
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.55,
  };
}

function dangerIconBtnStyle() {
  return {
    display: 'inline-flex', alignItems: 'center',
    background: C.redBg,
    color: C.red,
    border: `1px solid ${C.redBd}`,
    borderRadius: 7, padding: '7px 12px',
    fontFamily: F.body, fontSize: 12, fontWeight: 600,
    cursor: 'pointer',
  };
}

// ── Modals ────────────────────────────────────────────────────

function modalShellStyle() {
  return {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,.6)', zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  };
}
function modalCardStyle() {
  return {
    background: C.surface, borderRadius: 14,
    padding: '24px 26px', width: '100%', maxWidth: 480,
    boxShadow: '0 20px 60px rgba(0,0,0,.3)',
  };
}

function EditEmailModal({ entry, onClose, onDone }) {
  const { type, row } = entry;
  const { email: currentEmail, claimed } = getDisplayEmail(row, type);
  const [newEmail, setNewEmail] = useState(currentEmail || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [warning, setWarning] = useState(null);

  async function handleSave() {
    setErr(null); setWarning(null);
    const v = newEmail.trim();
    if (!v || !v.includes('@')) { setErr('Enter a valid email address.'); return; }
    if (v === (currentEmail || '')) { setErr('Email is unchanged.'); return; }
    setSaving(true);
    try {
      if (claimed) {
        const authId = row.user_id;
        if (!authId) throw new Error('Cannot reach auth account — no user_id on record.');
        const res = await adminSetUserEmail(authId, v);
        if (res?.warning) { setWarning(res.warning); setSaving(false); return; }
      } else {
        await updatePreClaimEmail(type, row.id, v);
      }
      onDone();
    } catch (e) {
      setErr(e.message || String(e));
      setSaving(false);
    }
  }

  return (
    <div style={modalShellStyle()}>
      <div style={modalCardStyle()}>
        <div style={{ fontFamily: F.display, fontSize: 18, color: C.charcoal, fontWeight: 600, marginBottom: 6 }}>Edit email</div>
        <div style={{ fontFamily: F.body, fontSize: 12, color: C.charMid, marginBottom: 14, lineHeight: 1.5 }}>
          {claimed
            ? 'Changes the login email on record. The user will need to sign in with the new email going forward.'
            : 'User has not yet claimed their invite — this updates the invited email only (no auth account exists yet).'}
        </div>
        <label style={labelStyle()}>Current</label>
        <div style={{
          fontFamily: F.mono, fontSize: 13, color: C.charMid,
          marginBottom: 14, padding: '9px 12px',
          background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: 7,
        }}>{currentEmail || '—'}</div>
        <label style={labelStyle()}>New email</label>
        <input value={newEmail} onChange={e => setNewEmail(e.target.value)} type="email" autoFocus style={inputStyle()} />
        {err && <div style={errBoxStyle()}>{err}</div>}
        {warning && <div style={warnBoxStyle()}>{warning}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={handleSave} disabled={saving} style={primaryBtnStyle(!saving)}>{saving ? 'Saving…' : 'Save email'}</button>
          <button onClick={onClose} disabled={saving} style={secondaryBtnStyle()}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function SetPasswordModal({ entry, onClose, onDone }) {
  const { type, row } = entry;
  const { email } = getDisplayEmail(row, type);
  const name = getDisplayName(entry);
  const authId = row.user_id;

  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null); // { password, message }
  const [copied, setCopied] = useState(false);

  function generate() { setPassword(genRandomPassword()); setShow(true); }

  async function handleSave() {
    setErr(null);
    if (!password || password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (!authId) { setErr('User has no auth account — cannot reset password.'); return; }
    setSaving(true);
    try {
      await adminSetUserPassword(authId, password);
      const firstName = (name || '').split(' ')[0] || 'there';
      const message =
`Hi ${firstName},

Your password for the Integro portal has been reset. You can now sign in with:

Email: ${email || '(your email)'}
Password: ${password}

We recommend changing it after you log in.`;
      setSuccess({ password, message });
    } catch (e) {
      setErr(e.message || String(e));
    }
    setSaving(false);
  }

  function copyMessage() {
    if (!success) return;
    navigator.clipboard.writeText(success.message);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div style={modalShellStyle()}>
      <div style={modalCardStyle()}>
        <div style={{ fontFamily: F.display, fontSize: 18, color: C.charcoal, fontWeight: 600, marginBottom: 6 }}>Set password</div>
        <div style={{ fontFamily: F.body, fontSize: 12, color: C.charMid, marginBottom: 14, lineHeight: 1.5 }}>
          For <strong>{name}</strong>{email ? <> · <span style={{ fontFamily: F.mono }}>{email}</span></> : null}
        </div>

        {success ? (
          <>
            <div style={{
              background: C.greenBg, border: `1px solid ${C.greenBd}`,
              borderRadius: 8, padding: '14px 16px', marginBottom: 14,
            }}>
              <div style={{ fontFamily: F.body, fontSize: 12, color: C.green, fontWeight: 700, marginBottom: 6 }}>Password updated.</div>
              <div style={{ fontFamily: F.mono, fontSize: 13, color: C.charcoal, wordBreak: 'break-all' }}>{success.password}</div>
            </div>
            <label style={labelStyle()}>Copy-paste message</label>
            <textarea readOnly value={success.message} rows={8}
              style={{ ...inputStyle(), fontFamily: F.body, resize: 'vertical', marginBottom: 10 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={copyMessage} style={primaryBtnStyle(true)}>{copied ? 'Copied!' : 'Copy message'}</button>
              <button onClick={onDone} style={secondaryBtnStyle()}>Done</button>
            </div>
          </>
        ) : (
          <>
            <label style={labelStyle()}>New password (min 8 chars)</label>
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <input value={password} onChange={e => setPassword(e.target.value)}
                type={show ? 'text' : 'password'} autoFocus
                style={{ ...inputStyle(), paddingRight: 42, fontFamily: F.mono, fontSize: 14 }} />
              <button onClick={() => setShow(s => !s)} type="button" title={show ? 'Hide' : 'Show'}
                style={{
                  position: 'absolute', right: 8, top: 8,
                  background: 'transparent', border: 'none',
                  color: C.charMid, cursor: 'pointer', padding: 6,
                }}>
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <button onClick={generate}
              style={{ ...secondaryBtnStyle(), padding: '7px 12px', fontSize: 12, marginBottom: 6, flex: 'none' }}>
              Generate random
            </button>
            {err && <div style={errBoxStyle()}>{err}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button onClick={handleSave} disabled={saving || !password} style={primaryBtnStyle(!saving && !!password)}>
                {saving ? 'Saving…' : 'Set password'}
              </button>
              <button onClick={onClose} disabled={saving} style={secondaryBtnStyle()}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DeleteModal({ entry, onClose, onDone }) {
  const { type } = entry;
  const name = getDisplayName(entry);
  const typeLabel = TYPE_LABEL[type];

  const [code] = useState(() => Math.floor(1000 + Math.random() * 9000).toString());
  const [step, setStep] = useState(1);
  const [input, setInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState(null);

  async function handleConfirm() {
    if (input !== code) return;
    setErr(null);
    setDeleting(true);
    try {
      if (type === 'advisor') {
        await deleteAdvisor(entry.row.id);
      } else if (type === 'client') {
        await deleteClientCascade(entry.row.id);
      } else if (type === 'teamMember') {
        await deleteTeamMember(entry.row.id);
      } else {
        throw new Error(`Unknown entry type: ${type}`);
      }
      onDone();
    } catch (e) {
      setErr(e.message || String(e));
      setDeleting(false);
    }
  }

  return (
    <div style={modalShellStyle()}>
      <div style={modalCardStyle()}>
        {step === 1 ? (
          <>
            <div style={{ fontFamily: F.display, fontSize: 18, color: C.red, fontWeight: 600, marginBottom: 4 }}>
              Delete {name}?
            </div>
            <div style={{
              fontFamily: F.body, fontSize: 10, color: C.charLight,
              textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600, marginBottom: 14,
            }}>
              {typeLabel}
            </div>
            <div style={{ fontFamily: F.body, fontSize: 13, color: C.charcoal, lineHeight: 1.6, marginBottom: 18 }}>
              This permanently removes this {typeLabel.toLowerCase()} and all associated data, including the auth login. This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(2)} style={{ ...primaryBtnStyle(true), background: C.red }}>Continue</button>
              <button onClick={onClose} style={secondaryBtnStyle()}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontFamily: F.display, fontSize: 18, color: C.red, fontWeight: 600, marginBottom: 6 }}>
              Final Verification
            </div>
            <div style={{ fontFamily: F.body, fontSize: 12, color: C.charMid, marginBottom: 14 }}>
              Type this code to confirm deletion of <strong>{name}</strong>:
            </div>
            <div style={{
              background: C.redBg, border: `2px solid ${C.red}`, borderRadius: 8,
              padding: 16, textAlign: 'center', marginBottom: 14,
            }}>
              <span style={{ fontFamily: F.mono, fontSize: 30, fontWeight: 700, color: C.red, letterSpacing: '.2em' }}>{code}</span>
            </div>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Type the code above"
              autoFocus
              style={{ ...inputStyle(), fontFamily: F.mono, fontSize: 18, textAlign: 'center', letterSpacing: '.2em' }}
            />
            {err && <div style={errBoxStyle()}>{err}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button
                onClick={handleConfirm}
                disabled={input !== code || deleting}
                style={{
                  ...primaryBtnStyle(input === code && !deleting),
                  background: (input === code && !deleting) ? C.red : C.bgAlt,
                }}>
                {deleting ? 'Deleting…' : 'Delete Permanently'}
              </button>
              <button onClick={onClose} disabled={deleting} style={secondaryBtnStyle()}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function labelStyle() {
  return {
    fontFamily: F.body, fontSize: 9, color: C.charMid,
    display: 'block', marginBottom: 5, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '.06em',
  };
}
function inputStyle() {
  return {
    width: '100%', background: C.bgAlt,
    border: `1px solid ${C.border}`, borderRadius: 7,
    padding: '10px 12px', color: C.charcoal,
    fontFamily: F.body, fontSize: 13, boxSizing: 'border-box',
  };
}
function primaryBtnStyle(enabled) {
  return {
    flex: 1, background: enabled ? C.navy : C.bgAlt,
    color: enabled ? '#fff' : C.charLight,
    border: 'none', borderRadius: 8, padding: '11px',
    fontFamily: F.body, fontSize: 13, fontWeight: 600,
    cursor: enabled ? 'pointer' : 'not-allowed',
  };
}
function secondaryBtnStyle() {
  return {
    flex: 1, background: C.bgAlt, color: C.charMid,
    border: `1px solid ${C.border}`, borderRadius: 8, padding: '11px',
    fontFamily: F.body, fontSize: 13, cursor: 'pointer',
  };
}
function errBoxStyle() {
  return {
    marginTop: 8, padding: '8px 12px',
    background: C.redBg, border: `1px solid ${C.redBd}`,
    borderRadius: 7, color: C.red,
    fontFamily: F.body, fontSize: 12,
  };
}
function warnBoxStyle() {
  return {
    marginTop: 8, padding: '8px 12px',
    background: C.amberBg, border: `1px solid ${C.amberBd}`,
    borderRadius: 7, color: C.amber,
    fontFamily: F.body, fontSize: 12,
  };
}
